import { authService } from './authService';

/**
 * Pictures in the editor have no login header and no token any more (owner 2026-10-31): the browser sends a protected cookie
 * for media files only. A picture may ask before the cookie has arrived, for example right after login. It then fails once,
 * the cookie is fetched, and the picture is loaded again. Public sites need none of this.
 */
const BASE = `${import.meta.env.VITE_API_URL || ''}/api/saas/v1/media/session`;
const RENEW_MS = 30 * 60_000;
const FILE = /\/api\/saas\/v1\/media\/[a-f0-9]{24}\/file/;

let pending: Promise<boolean> | null = null;
let lastOk = 0;

export function ensureMediaSession(force = false): Promise<boolean> {
  const token = authService.getToken();
  if (!token) return Promise.resolve(false);
  if (!force && Date.now() - lastOk < RENEW_MS) return Promise.resolve(true);
  if (pending) return pending;
  pending = fetch(BASE, { method: 'POST', credentials: 'include', headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (r.ok) lastOk = Date.now();
      return r.ok;
    })
    .catch(() => false)
    .finally(() => {
      pending = null;
    });
  return pending;
}

function clearMediaSession(): void {
  lastOk = 0;
  void fetch(BASE, { method: 'DELETE', credentials: 'include' }).catch(() => undefined);
}

/** True once per element and address: a picture that really is missing or forbidden must not retry for ever. */
export function shouldRetryMedia(el: { getAttribute(n: string): string | null; setAttribute(n: string, v: string): void }, src: string): boolean {
  if (!FILE.test(src) || el.getAttribute('data-media-retried') === src) return false;
  el.setAttribute('data-media-retried', src);
  return true;
}

function onMediaError(e: Event): void {
  const el = e.target;
  if (!(el instanceof HTMLImageElement || el instanceof HTMLVideoElement || el instanceof HTMLAudioElement)) return;
  const src = el.currentSrc || el.src;
  if (!authService.getToken() || !shouldRetryMedia(el, src)) return;
  void ensureMediaSession(true).then((ok) => {
    if (!ok) return;
    if (el instanceof HTMLImageElement) {
      const current = el.src;
      el.src = '';
      el.src = current;
    } else {
      el.load();
    }
  });
}

export function startMediaSession(): () => void {
  let hadLogin = Boolean(authService.getToken());
  const onAuth = () => {
    const has = Boolean(authService.getToken());
    if (has) void ensureMediaSession(!hadLogin);
    else if (hadLogin) clearMediaSession();
    hadLogin = has;
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') void ensureMediaSession();
  };
  void ensureMediaSession(true);
  const timer = window.setInterval(() => void ensureMediaSession(), RENEW_MS);
  window.addEventListener('platzona-auth-change', onAuth);
  document.addEventListener('visibilitychange', onVisible);
  document.addEventListener('error', onMediaError, true);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener('platzona-auth-change', onAuth);
    document.removeEventListener('visibilitychange', onVisible);
    document.removeEventListener('error', onMediaError, true);
  };
}
